import React from 'react';
import { lighten } from './chartTokens';

/* ──────────────────────────────────────────────────────────────────────────
   ChartDefs — the signature SVG <defs> (glow filter + stroke/fill gradients) from
   spec §2. Colours come in already resolved from the v4 tokens (useChartTheme), so
   the defs re-theme with the app and the glow dials back in light mode.

   Rendered as a hidden, zero-size <svg> SIBLING of the chart, not a child of it:
   Recharts filters unrecognised component children out of its own <svg>, so a
   <defs> handed to it never reaches the DOM. SVG ids are document-global, so the
   chart's url(#id) refs resolve to these defs regardless of which <svg> holds them.
   Ids are namespaced per chart instance (makeChartIds) to avoid global collisions.
   ────────────────────────────────────────────────────────────────────────── */

/* Line stroke gradient: bright top stop (derived FROM the token) → the token. */
const LineGrad = ({ id, color }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor={lighten(color, 0.45)} />
    <stop offset="100%" stopColor={color} />
  </linearGradient>
);

/* Area fill gradient: the token hue → transparent. */
const AreaGrad = ({ id, color }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor={color} stopOpacity="0.32" />
    <stop offset="100%" stopColor={color} stopOpacity="0" />
  </linearGradient>
);

const ChartDefs = ({ ids, colors }) => {
  const tones = Object.keys(colors.tone);
  return (
    <svg
      aria-hidden="true"
      width="0"
      height="0"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      <defs>
        {/* THE GLOW — the signature. Doubled feMergeNode = more intense bloom (§2).
            stdDeviation is the dark/light dial (§5). */}
        <filter id={ids.glow} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation={colors.glowStd} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Line + area gradients per directional tone. */}
        {tones.map((t) => (
          <React.Fragment key={t}>
            <LineGrad id={ids.line[t]} color={colors.tone[t]} />
            <AreaGrad id={ids.area[t]} color={colors.tone[t]} />
          </React.Fragment>
        ))}

        {/* Bar fill — translucent warm gold over a near-transparent base (§2). */}
        <linearGradient id={ids.bar} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colors.tone.gold} stopOpacity="0.30" />
          <stop offset="100%" stopColor={colors.tone.gold} stopOpacity="0.04" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export default ChartDefs;
